import React from 'react';
import { format, parse, isValid } from 'date-fns';
import { Calendar as CalendarIcon } from 'lucide-react';
import { FieldError, tintFromAccent } from './_shared';
import CalendarPopover from './CalendarPopover';

interface DateFieldProps {
  id: string;
  label: string;
  required?: boolean;
  error?: string;
  hint?: React.ReactNode;
  value: string;          // YYYY-MM-DD or empty
  onChange: (next: string) => void;
  min?: string;           // YYYY-MM-DD inclusive
  max?: string;
  /** Tooltip shown when hovering a day that's earlier than `min`. */
  minDisabledMessage?: string;
  /** Tooltip shown when hovering a day that's later than `max`. */
  maxDisabledMessage?: string;
  accentColor: string;
  primaryColor?: string;  // deep brand color used for text/captions; defaults to slate-900
}

const DateField: React.FC<DateFieldProps> = ({
  id, label, required, error, hint, value, onChange, min, max,
  minDisabledMessage, maxDisabledMessage, accentColor, primaryColor,
}) => {
  const [open, setOpen] = React.useState(false);
  const rootRef = React.useRef<HTMLDivElement | null>(null);
  const triggerRef = React.useRef<HTMLButtonElement | null>(null);

  const selected = value ? parse(value, 'yyyy-MM-dd', new Date()) : undefined;
  const display = selected && isValid(selected) ? format(selected, 'EEE, MMM d, yyyy') : '';

  return (
    <div
      ref={rootRef}
      className="relative"
      style={{ ['--accent' as string]: accentColor }}
    >
      {/* Floating-label-style trigger styled to match the rest of the wizard.
          Permanent soft background + brand-tinted border to make it read as
          interactive without being loud. */}
      <div
        className="relative px-5 pt-2 rounded-xl transition-all cursor-pointer hover:brightness-[0.98] group"
        style={{
          backgroundColor: tintFromAccent(accentColor, 0.05),
          border: `1px solid ${tintFromAccent(accentColor, 0.18)}`,
        }}
        onClick={() => triggerRef.current?.click()}
      >
        <span
          aria-hidden
          className={
            error
              ? 'absolute left-0 top-3 bottom-3 w-[2px] rounded-r bg-rose-600 opacity-100 transition-opacity'
              : 'absolute left-0 top-3 bottom-3 w-[2px] rounded-r bg-[color:var(--accent)] opacity-0 group-focus-within:opacity-100 transition-opacity'
          }
        />
        <label
          htmlFor={id}
          className={`absolute left-5 top-3 pointer-events-none select-none text-[11px] font-medium tracking-[0.08em] uppercase ${
            error ? 'text-rose-600' : 'text-[color:var(--accent)]'
          }`}
        >
          {label}
          {required && (
            <span className={`ml-1 ${error ? 'text-rose-600' : 'text-[color:var(--accent)]'}`} aria-hidden>•</span>
          )}
        </label>
        <button
          ref={triggerRef}
          id={id}
          type="button"
          onClick={(e) => { e.stopPropagation(); setOpen((v) => !v); }}
          className="group/trigger w-full flex items-center gap-2 bg-transparent border-0 px-0 pt-7 pb-2 text-[15px] text-slate-900 cursor-pointer focus:outline-none focus:ring-0"
        >
          <span className="flex-1 text-left">
            {display || <span className="text-slate-300">Pick a date</span>}
          </span>
          <CalendarIcon
            className="w-4 h-4 transition-colors"
            style={{ color: open ? accentColor : 'rgb(148, 163, 184)' }}
          />
        </button>
        <FieldError message={error} className="mb-2" />
        {hint && <div className="pb-1">{hint}</div>}
      </div>

      <CalendarPopover
        open={open}
        onClose={() => setOpen(false)}
        anchorRef={rootRef}
        value={value}
        onChange={onChange}
        min={min}
        max={max}
        minDisabledMessage={minDisabledMessage}
        maxDisabledMessage={maxDisabledMessage}
        accentColor={accentColor}
        primaryColor={primaryColor}
      />
    </div>
  );
};

export default DateField;
